import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, TrendingUp, Settings, Activity } from 'lucide-react';
import './PaymentGatewayManagement.css';

// Import the api function from parent
const api = (path, opts={}) => {
  const base = '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers||{})
    }
  });
};

function PaymentGatewayManagement() {
  const apiWithAccessControl = api;
  const [activeTab, setActiveTab] = useState('gateways');
  const [gateways, setGateways] = useState([]);
  const [serviceCharges, setServiceCharges] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Gateway editing state
  const [editingGateway, setEditingGateway] = useState(null);
  const [showGatewayEdit, setShowGatewayEdit] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Load all data
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [gatewaysRes, serviceChargesRes, statsRes] = await Promise.all([
        apiWithAccessControl('/admin/payments/gateways'),
        apiWithAccessControl('/admin/payments/service-charges'),
        apiWithAccessControl('/admin/payments/statistics')
      ]);

      if (gatewaysRes.ok) {
        const data = await gatewaysRes.json();
        console.log(`[Payment Gateway] Loaded ${data.gateways?.length || 0} gateways`);
        setGateways(data.gateways || []);
      } else {
        const errData = await gatewaysRes.json();
        console.error('[Payment Gateway] Failed to load gateways:', errData);
      }

      if (serviceChargesRes.ok) {
        const data = await serviceChargesRes.json();
        setServiceCharges(data.serviceCharges || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStatistics(data.statistics || {
          totalTransactions: 0,
          totalVolume: 0,
          totalServiceCharges: 0,
          averageTransactionValue: 0,
          successRate: 0,
          topGateways: [],
          serviceChargeBreakdown: {}
        });
      }
    } catch (e) {
      console.error('[Payment Gateway] Error loading data:', e);
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle gateway edit
  const handleEditGateway = (gateway) => {
    console.log(`[Payment Gateway] Editing gateway: ${gateway.displayName}`);
    setEditingGateway(gateway);
    setShowGatewayEdit(true);
    setTestResult(null);
    setError('');
    setSuccess('');
  };

  // Handle gateway update
  const handleUpdateGateway = async (gatewayData) => {
    try {
      setError('');
      setSuccess('');

      const response = await apiWithAccessControl(`/admin/payments/gateways/${editingGateway.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gatewayData)
      });

      if (response.ok) {
        setSuccess('Gateway updated successfully');
        setShowGatewayEdit(false);
        setEditingGateway(null);
        loadData();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update gateway');
      }
    } catch (e) {
      setError('Failed to update gateway');
      console.error('[Payment Gateway] Update error:', e);
    }
  };

  // Test gateway connection
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError('');

      const publicKey = document.getElementById('editPublicKey')?.value?.trim();
      const secretKey = document.getElementById('editSecretKey')?.value?.trim();

      if (!publicKey || !secretKey) {
        setError('Please enter API keys first');
        setTesting(false);
        return;
      }

      console.log(`[Payment Gateway] Testing connection for gateway: ${editingGateway.displayName}`);

      const response = await apiWithAccessControl(`/admin/payments/gateways/${editingGateway.id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credentials: { publicKey, secretKey }
        })
      });

      const data = await response.json();
      console.log(`[Payment Gateway] Test result: ${data.success ? 'Success' : 'Failed'}`);

      if (data.success) {
        setTestResult({
          success: true,
          message: data.testResult.message,
          responseTime: data.testResult.responseTime,
          accountStatus: data.testResult.accountStatus
        });
        setSuccess('Connection test successful!');
      } else {
        setTestResult({
          success: false,
          message: data.testResult?.message || data.error || 'Connection test failed',
          error: data.testResult?.error
        });
        setError(data.error || 'Connection test failed');
      }
    } catch (e) {
      console.error('[Payment Gateway] Test error:', e);
      setError('Failed to test connection');
      setTestResult({
        success: false,
        message: 'Network error during test',
        error: e.message
      });
    } finally {
      setTesting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="pgm-container">
        <div className="pgm-loading-state">
          <Activity className="spin" size={32} />
          <p>Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pgm-container">
      <div className="pgm-header">
        <h1><CreditCard size={28} /> Payment Gateway Management</h1>
        <p>Configure payment gateways, service charges, and view transaction statistics</p>
      </div>

      {error && (
        <div className="pgm-alert pgm-alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="pgm-alert pgm-alert-success">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="pgm-tabs">
        <button
          className={`pgm-tab ${activeTab === 'gateways' ? 'active' : ''}`}
          onClick={() => setActiveTab('gateways')}
        >
          <CreditCard size={18} /> Gateways
        </button>
        <button
          className={`pgm-tab ${activeTab === 'service-charges' ? 'active' : ''}`}
          onClick={() => setActiveTab('service-charges')}
        >
          <DollarSign size={18} /> Service Charges
        </button>
        <button
          className={`pgm-tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          <TrendingUp size={18} /> Statistics
        </button>
      </div>

      {/* Gateways Tab */}
      {activeTab === 'gateways' && (
        <div className="pgm-tab-content">
          <h2>Payment Gateways</h2>
          <div className="pgm-gateways-grid">
            {gateways.length === 0 ? (
              <div className="pgm-empty-state">
                <CreditCard size={48} />
                <p>No payment gateways configured</p>
              </div>
            ) : (
              gateways.map((gateway) => (
                <div key={gateway.id} className={`pgm-gateway-card ${gateway.isActive ? 'active' : 'inactive'}`}>
                  <div className="pgm-gateway-header">
                    <div className="pgm-gateway-icon">{gateway.icon || '💳'}</div>
                    <div className="pgm-gateway-info">
                      <h3>{gateway.displayName}</h3>
                      <p className="pgm-gateway-name">{gateway.name}</p>
                    </div>
                    <div className={`pgm-gateway-status ${gateway.isActive ? 'pgm-status-active' : 'pgm-status-inactive'}`}>
                      {gateway.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  <div className="pgm-gateway-details">
                    <p>{gateway.description}</p>
                    <div className="pgm-gateway-meta">
                      <span className={`pgm-mode-badge ${gateway.testMode ? 'test' : 'live'}`}>
                        {gateway.testMode ? 'Test Mode' : 'Live Mode'}
                      </span>
                      {gateway.isLive && <span className="pgm-live-indicator">🟢 Live</span>}
                    </div>
                  </div>

                  <div className="pgm-gateway-actions">
                    <button
                      className="pgm-btn pgm-btn-primary"
                      onClick={() => handleEditGateway(gateway)}
                    >
                      <Settings size={16} /> Configure
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Service Charges Tab */}
      {activeTab === 'service-charges' && (
        <div className="pgm-tab-content">
          <h2>Service Charge Configuration</h2>
          <div className="pgm-service-charges-table">
            {serviceCharges.length === 0 ? (
              <div className="pgm-empty-state">
                <DollarSign size={48} />
                <p>No service charges configured</p>
                <small>Service charges are automatically applied based on merchant subscription tier</small>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Rate</th>
                    <th>Who Pays</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceCharges.map((charge) => (
                    <tr key={charge.id}>
                      <td className="pgm-tier-name">{charge.name || charge.tier}</td>
                      <td className="pgm-rate">{charge.rate}%</td>
                      <td className="pgm-who-pays">{charge.whoPays}</td>
                      <td>{charge.description || '-'}</td>
                      <td>
                        <span className={`pgm-status-badge ${charge.isActive ? 'active' : 'inactive'}`}>
                          {charge.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && statistics && (
        <div className="pgm-tab-content">
          <h2>Payment Statistics</h2>

          <div className="pgm-stats-grid">
            <div className="pgm-stat-card">
              <div className="pgm-stat-icon"><Activity size={24} /></div>
              <div className="pgm-stat-info">
                <p className="pgm-stat-label">Total Transactions</p>
                <p className="pgm-stat-value">{statistics.totalTransactions.toLocaleString()}</p>
              </div>
            </div>

            <div className="pgm-stat-card">
              <div className="pgm-stat-icon"><DollarSign size={24} /></div>
              <div className="pgm-stat-info">
                <p className="pgm-stat-label">Total Volume</p>
                <p className="pgm-stat-value">{formatCurrency(statistics.totalVolume)}</p>
              </div>
            </div>

            <div className="pgm-stat-card">
              <div className="pgm-stat-icon"><TrendingUp size={24} /></div>
              <div className="pgm-stat-info">
                <p className="pgm-stat-label">Service Charges</p>
                <p className="pgm-stat-value">{formatCurrency(statistics.totalServiceCharges)}</p>
              </div>
            </div>

            <div className="pgm-stat-card">
              <div className="pgm-stat-icon"><CreditCard size={24} /></div>
              <div className="pgm-stat-info">
                <p className="pgm-stat-label">Avg Transaction</p>
                <p className="pgm-stat-value">{formatCurrency(statistics.averageTransactionValue)}</p>
              </div>
            </div>

            <div className="pgm-stat-card">
              <div className="pgm-stat-icon"><Activity size={24} /></div>
              <div className="pgm-stat-info">
                <p className="pgm-stat-label">Success Rate</p>
                <p className="pgm-stat-value">{statistics.successRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          {statistics.topGateways && statistics.topGateways.length > 0 && (
            <div className="pgm-top-gateways">
              <h3>Top Payment Gateways</h3>
              <div className="pgm-gateways-list">
                {statistics.topGateways.map((gw, idx) => (
                  <div key={idx} className="pgm-gateway-stat">
                    <div className="pgm-gateway-stat-header">
                      <span className="pgm-gateway-stat-name">{gw.name}</span>
                      <span className="pgm-gateway-stat-count">{gw.transactions} transactions</span>
                    </div>
                    <div className="pgm-gateway-stat-amount">{formatCurrency(gw.volume)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gateway Edit Modal */}
      {showGatewayEdit && editingGateway && (
        <div className="pgm-modal-overlay" onClick={() => setShowGatewayEdit(false)}>
          <div className="pgm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pgm-modal-header">
              <h2>Configure {editingGateway.displayName}</h2>
              <button className="pgm-modal-close" onClick={() => setShowGatewayEdit(false)}>×</button>
            </div>

            <div className="pgm-modal-body">
              {/* Current Status Banner */}
              <div className="pgm-status-banner">
                <div className="pgm-status-item">
                  <span className="pgm-status-item-label">Status:</span>
                  <span className={`pgm-status-item-value ${editingGateway.isActive ? 'active' : 'inactive'}`}>
                    {editingGateway.isActive ? '🟢 Enabled' : '🔴 Disabled'}
                  </span>
                </div>
                <div className="pgm-status-item">
                  <span className="pgm-status-item-label">Environment:</span>
                  <span className={`pgm-status-item-value ${editingGateway.testMode ? 'test' : 'live'}`}>
                    {editingGateway.testMode ? '🧪 Test' : '🚀 Live'}
                  </span>
                </div>
              </div>

              <div className="pgm-info-notice info">
                <strong>ℹ️ Note:</strong> The environment (Test/Live) is determined by your API keys.
                Use test keys (pk_test_...) for testing, and live keys (pk_live_...) for production.
              </div>

              <div className="pgm-info-notice warning">
                <strong>🔒 Security:</strong> API keys are masked for security (e.g., sk_•••••••••1234).
                Enter your full API key to update the gateway configuration.
              </div>

              <div className="pgm-form-group">
                <label htmlFor="editPublicKey">Public Key / API Key</label>
                <input
                  type="text"
                  id="editPublicKey"
                  defaultValue={editingGateway.credentials?.publicKey || ''}
                  placeholder="Enter public key (e.g., pk_test_...)"
                />
                <small className="pgm-input-hint">
                  {editingGateway.testMode ? 'Use test key (starts with pk_test_)' : 'Use live key (starts with pk_live_)'}
                </small>
              </div>

              <div className="pgm-form-group">
                <label htmlFor="editSecretKey">Secret Key</label>
                <input
                  type="password"
                  id="editSecretKey"
                  defaultValue={editingGateway.credentials?.secretKey || ''}
                  placeholder="Enter secret key (e.g., sk_test_...)"
                />
                <small className="pgm-input-hint">
                  {editingGateway.testMode ? 'Use test key (starts with sk_test_)' : 'Use live key (starts with sk_live_)'}
                </small>
              </div>

              <div className="pgm-form-group pgm-checkbox-group">
                <label className="pgm-checkbox-label">
                  <input
                    type="checkbox"
                    id="editIsActive"
                    defaultChecked={editingGateway.isActive}
                  />
                  <span className="pgm-checkbox-text">
                    <strong>Enable this payment gateway</strong>
                    <small>Allow merchants to use this gateway for payments</small>
                  </span>
                </label>
              </div>

              {testResult && (
                <div className={`pgm-test-result ${testResult.success ? 'success' : 'error'}`}>
                  <h4>{testResult.success ? '✓ Success' : '✗ Failed'}</h4>
                  <p>{testResult.message}</p>
                  {testResult.responseTime && <p>Response Time: {testResult.responseTime}ms</p>}
                  {testResult.accountStatus && <p>Account: {testResult.accountStatus}</p>}
                  {testResult.error && <p className="pgm-error-details">{testResult.error}</p>}
                </div>
              )}
            </div>

            <div className="pgm-modal-footer">
              <button
                className="pgm-btn pgm-btn-secondary"
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                className="pgm-btn pgm-btn-primary"
                onClick={() => {
                  const publicKey = document.getElementById('editPublicKey').value?.trim();
                  const secretKey = document.getElementById('editSecretKey').value?.trim();
                  const isActive = document.getElementById('editIsActive').checked;

                  handleUpdateGateway({
                    credentials: { publicKey, secretKey },
                    isActive
                  });
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentGatewayManagement;
